import type { Quest } from './types'

/**
 * Nothing in the Unity importer detects a prereq cycle: two quests that each
 * require the other both silently never appear, with no warning anywhere. This is
 * the one error class the import validation misses, so the editor checks it.
 */
export interface GraphProblem {
  kind: 'cycle' | 'missing'
  questIds: string[]
  message: string
}

export function findPrereqProblems(quests: Quest[]): GraphProblem[] {
  const byId = new Map(quests.map((quest) => [quest.id, quest]))
  const problems: GraphProblem[] = []

  const dependencies = (quest: Quest): string[] =>
    quest.prereqs.filter((p) => p.type === 'QuestCompleted').map((p) => p.quest)

  for (const quest of quests) {
    for (const dependency of dependencies(quest)) {
      if (!byId.has(dependency)) {
        problems.push({
          kind: 'missing',
          questIds: [quest.id],
          message: `${quest.id} requires "${dependency}", which does not exist.`,
        })
      }
    }
  }

  // Iterative DFS with an explicit stack — a deep chain shouldn't risk the call stack.
  const UNVISITED = 0
  const IN_PROGRESS = 1
  const DONE = 2
  const state = new Map<string, number>()
  const seenCycles = new Set<string>()

  for (const start of byId.keys()) {
    if (state.get(start) === DONE) continue
    const path: string[] = []
    const stack: { id: string; index: number }[] = [{ id: start, index: 0 }]
    state.set(start, IN_PROGRESS)
    path.push(start)

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!
      const quest = byId.get(frame.id)
      const deps = quest ? dependencies(quest) : []

      if (frame.index >= deps.length) {
        state.set(frame.id, DONE)
        stack.pop()
        path.pop()
        continue
      }

      const next = deps[frame.index]!
      frame.index += 1
      if (!byId.has(next)) continue

      const nextState = state.get(next) ?? UNVISITED
      if (nextState === IN_PROGRESS) {
        const cycle = path.slice(path.indexOf(next))
        // Normalise rotation so the same cycle isn't reported once per entry point.
        const key = [...cycle].sort().join('>')
        if (!seenCycles.has(key)) {
          seenCycles.add(key)
          problems.push({
            kind: 'cycle',
            questIds: cycle,
            message: `Prereq cycle: ${[...cycle, next].join(' → ')}. None of these quests will ever appear.`,
          })
        }
      } else if (nextState === UNVISITED) {
        state.set(next, IN_PROGRESS)
        path.push(next)
        stack.push({ id: next, index: 0 })
      }
    }
  }

  return problems
}
